import React from 'react';
import _ = require('underscore');

import { Filter } from '~commonInfra/utils/Filter';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { EditConnectionDialogBridgeInfo } from '~src/connectivity/components/EditConnection/components/EditConnectionActionDialogBridgeInfo';
import { EditableConnection } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';

import { EditOAuthConnectionDialogProps, EditOAuthConnectionDialogState } from '../../EditOAuthConnectionAction';
import { EditSalesforceConnectionDialogEmbeddedToggle } from './EditSalesforceConnectionDialogEmbeddedToggle';
import { EditSalesforceConnectionDialogPrompt } from './EditSalesforceConnectionDialogPrompt';
import { EditSalesforceConnectionDialogTestConnection } from './EditSalesforceDialogTestConnection';

export type EditSalesforceConnectionDialogBodyProps = ReactActionDialogBodyProps<
  EditOAuthConnectionDialogProps,
  EditOAuthConnectionDialogState
>;

export function EditSalesforceConnectionDialogBody(props: EditSalesforceConnectionDialogBodyProps) {

  function updateFormState<K extends keyof EditOAuthConnectionDialogState>(
    field: K,
    value: EditOAuthConnectionDialogState[K]
  ) {
    props.updateDialogState({ [field]: value });
  }

  const refreshCredentials = (
    firstConnectionType: string) => {
    const credentialsQuery: ResourceQuery<IOAuthCredential> =
      OAuthCredentials.all().addFilter(Filter.fieldEq('type', firstConnectionType));

    return SliceableHelper.fetchAll(credentialsQuery)
      .then((credentialsListResult: ListResult<IOAuthCredential>) => {
        props.updateDialogState({ credentials: credentialsListResult.items });
        return credentialsListResult; // For chaining
      });
  };

  const handleToggleChange = (credentialOptionName: string) => {
    if (credentialOptionName === 'oauth') {
      props.updateDialogState({ credentialOption: 'oauth' });
    } else if (credentialOptionName === 'password') {
      props.updateDialogState({ credentialOption: 'password', isPasswordEmbedded: true });
    } else if (credentialOptionName === 'none') {
      props.updateDialogState({ credentialOption: 'none' });
    }
  };

  const handleUserNameChange = (username: string) => {
    props.updateDialogState({ username: username });
  };

  const handlePasswordChange = (password: string) => {
    props.updateDialogState({ password: password });
  };
  const handleCredentialIdChange = (credentialId: string) => {
    if (credentialId === props.addNewOAuthCredentialOptionValue) {
      props.updateDialogState({
        credentialId: undefined,
      });
      const firstConnection: EditableConnection = props.firstConnection;
      const firstConnectionType = firstConnection.connectionType;
      const firstConnectionDetails = firstConnection.connectionDetails;
      let instanceUrl: string;
      let role: string;
      if (firstConnectionDetails.type === 'database') {
        instanceUrl = firstConnectionDetails.instanceUrl;
        role = firstConnectionDetails.role;
      }

      AddOAuthCredentialAction.attemptAuth(firstConnectionType, instanceUrl, role).then((credentialId: string) => {
        refreshCredentials(firstConnectionType).then((credentialsListResult: ListResult<IOAuthCredential>) => {
          // Set the credentialId if it exists in the list
          if (_.find(credentialsListResult.items, (credential: IOAuthCredential) => credential.id === credentialId)) {
            props.updateDialogState({
              credentialId: credentialId,
            });
          }
        });
      });
    } else {
      props.updateDialogState({ credentialId: credentialId });
    }
  };

  return (
    <>
      <EditSalesforceConnectionDialogPrompt prompt={props.prompt} />
      <div style={{ width: '100%' }}>
        <EditSalesforceConnectionDialogEmbeddedToggle
          credentialNoValueOptionText={props.dialogState.credentialNoValueOptionText}
          currentConnectionTypeDisplayName={props.currentConnectionTypeDisplayName}
          addNewOAuthCredentialOptionValue={props.addNewOAuthCredentialOptionValue}
          credentialId={props.dialogState.credentialId}
          credentials={props.dialogState.credentials}
          credentialOption={props.dialogState.credentialOption}
          username={props.dialogState.username}
          password={props.dialogState.password}
          handleToggleChange={handleToggleChange}
          handleCredentialIdChange={handleCredentialIdChange}
          handleUserNameChange={handleUserNameChange}
          handlePasswordChange={handlePasswordChange}
        />
        <EditConnectionDialogBridgeInfo
          usingRemoteQueryAgent={props.dialogState.usingRemoteQueryAgent}
          handleUsingRemoteQueryAgentChange={(value) => updateFormState('usingRemoteQueryAgent', value)}
          showNetworkType={props.showNetworkType}
        />
        <EditSalesforceConnectionDialogTestConnection
          dialogState={props.dialogState}
          onTestConnection={props.testConnection}
          isTestConnectionDisabled={props.isTestConnectionDisabled(props.dialogState)}
        />
      </div>
    </>
  );
}
